'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AccessRequestNodeFragment } from '../gql/fragments/generated/AccessRequestNode';

/**
 * Context type for managing evicted access requests.
 *
 * This context tracks access requests that have been optimistically removed
 * from the cache but need to be dismissed when the user leaves the page.
 *
 * This is used alongside the optimisticallyDeclineRequest function in useAccessRequestActions.
 * When an admin clicks decline on an access request, the request is removed from
 * the cache and added to the list of evicted items. The admin can undo the decline action
 * and remove it from the evicted list. Otherwise, all items in the evicted list are dismissed
 * after a timeout, or when the user leaves the page.
 */
type EvictedAccessRequestsContextType = {
  /** Array of evicted access requests with their dismiss handlers */
  evictedAccessRequests: Array<{
    accessRequest: AccessRequestNodeFragment;
    dismissHandler: () => void;
  }>;
  /**
   * Adds an access request to the evicted list with its dismiss handler
   *
   * @param accessRequest - The access request to track
   * @param dismissHandler - Function to call when dismissing the request
   */
  addEvictedAccessRequest: (
    accessRequest: AccessRequestNodeFragment,
    dismissHandler: () => void
  ) => void;
  /**
   * Removes an access request from the evicted list
   *
   * @param id - The ID of the access request to remove
   */
  removeEvictedAccessRequest: (id: string) => void;
};

const EvictedAccessRequestsContext =
  createContext<EvictedAccessRequestsContextType>({
    evictedAccessRequests: [],
    addEvictedAccessRequest: () => undefined,
    removeEvictedAccessRequest: () => undefined,
  });

/**
 * Hook to access the evicted access requests context.
 *
 * This hook provides access to the list of evicted access requests and
 * methods to add or remove them. Evicted access requests are tracked so
 * they can be properly dismissed when the user leaves the page.
 *
 * @throws {Error} If used outside of EvictedAccessRequestsContextProvider
 * @returns {EvictedAccessRequestsContextType} The context value
 *
 * @example
 * ```tsx
 * const { addEvictedAccessRequest, removeEvictedAccessRequest } =
 *   useEvictedAccessRequests();
 *
 * // Add an evicted request
 * addEvictedAccessRequest(accessRequest, () => {
 *   // Dismiss handler logic
 * });
 * ```
 */
export const useEvictedAccessRequests = () => {
  const context = useContext(EvictedAccessRequestsContext);
  if (!context) {
    throw new Error(
      'useEvictedAccessRequests must be used within an EvictedAccessRequestsProvider'
    );
  }
  return context;
};

export const EvictedAccessRequestsContextProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const [evictedAccessRequests, setEvictedAccessRequests] = useState<
    Array<{
      accessRequest: AccessRequestNodeFragment;
      dismissHandler: () => void;
    }>
  >([]);

  /**
   * Adds an access request to the evicted list.
   * Prevents duplicates by checking if the request already exists.
   */
  const addEvictedAccessRequest = useCallback(
    (accessRequest: AccessRequestNodeFragment, dismissHandler: () => void) => {
      setEvictedAccessRequests((prev) => {
        const exists = prev.some(
          (item) => item.accessRequest.id === accessRequest.id
        );
        if (exists) {
          return prev;
        }
        return [...prev, { accessRequest, dismissHandler }];
      });
    },
    []
  );

  /**
   * Removes an access request from the evicted list by ID.
   */
  const removeEvictedAccessRequest = useCallback((id: string) => {
    setEvictedAccessRequests((prev) =>
      prev.filter((item) => item.accessRequest.id !== id)
    );
  }, []);

  useEffect(() => {
    /**
     * Dismisses all evicted access requests by calling their handlers
     * and clearing the list.
     */
    const dismissAllEvictedAccessRequests = () => {
      evictedAccessRequests.forEach(({ dismissHandler }) => {
        dismissHandler();
      });
      setEvictedAccessRequests([]);
    };

    const handleBeforeUnload = () => {
      dismissAllEvictedAccessRequests();
    };

    const handlePageHide = () => {
      dismissAllEvictedAccessRequests();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [evictedAccessRequests]);

  const value = useMemo(
    () => ({
      evictedAccessRequests,
      addEvictedAccessRequest,
      removeEvictedAccessRequest,
    }),
    [evictedAccessRequests, addEvictedAccessRequest, removeEvictedAccessRequest]
  );

  return (
    <EvictedAccessRequestsContext.Provider value={value}>
      {children}
    </EvictedAccessRequestsContext.Provider>
  );
};
